import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { ReactElement, useState } from "react";

import ChipGroup from "../../../components/ChipsGroup/ChipsGroup";
import Container from "../../../components/Container/Container";
import { Fund } from "../../../utils/types/investor.type";
import {
  useFundsListEffect,
  useUpdateFundsListEffect,
} from "./InvestorFunds.hooks";
import {
  ChipsContent,
  FundContent,
  SearchBarInput,
  SearchInputAdornment,
  StyledChecksCard,
  StyledFormControlLabel,
  StyledFormGroup,
  StyledParentCard,
} from "./InvestorFunds.style";

type Props = {
  investorFundList: Fund[];
  setFundListUpdated: any;
};

const InvestorFunds: React.FC<Props> = ({
  investorFundList,
  setFundListUpdated,
}: Props): ReactElement => {
  const { fundList } = useFundsListEffect();

  const { checkFundList, setCheckFundList } = useUpdateFundsListEffect(
    investorFundList,
    fundList
  );

  const [searchVal, setSearchVal] = useState<string>("");

  const handleOnChange = ({ target }: any) => {
    const { value, id } = target;

    setSearchVal(value);
    const filteredList: any = fundList.filter((item) =>
      item.name.toLowerCase().includes(value.toLowerCase())
    );

    setCheckFundList([...filteredList]);
    setFundListUpdated(true);
  };

  const onFundClick = ({ target }: any) => {
    const { checked, id } = target;

    const curItem = checkFundList.findIndex((fund) => fund.id === id);

    checkFundList[curItem].isChecked = !checkFundList[curItem].isChecked;

    const newFund = Object({
      id: checkFundList[curItem].id,
      name: checkFundList[curItem].name,
      isChecked: checkFundList[curItem].isChecked,
    });

    if (checked) {
      investorFundList.push(newFund);
    } else {
      const chipIndex = investorFundList.findIndex(
        (chipFund) => chipFund.id === id
      );

      investorFundList.splice(chipIndex, 1);
    }
    setCheckFundList([...checkFundList]);
    setFundListUpdated(true);
  };

  const onCheckAll = ({ target }: any) => {
    const { checked } = target;

    const AllChecked = checkFundList.map((fund) => {
      return { ...fund, isChecked: checked };
    });

    setCheckFundList([...AllChecked]);

    if (checked) {
      for (const checkFund of checkFundList) {
        if (!investorFundList.some((m) => m.id === checkFund.id)) {
          const newFund = Object({});

          newFund.id = checkFund.id;
          newFund.name = checkFund.name;
          investorFundList.push(newFund);
        }
      }
    } else {
      investorFundList.splice(0, investorFundList.length);
    }

    setFundListUpdated(true);
  };

  const onRemoveChip = (id: string) => {
    const curItem = checkFundList.findIndex((fund) => fund.id === id);

    checkFundList[curItem].isChecked = !checkFundList[curItem].isChecked;

    setCheckFundList([...checkFundList]);

    const chipIndex = investorFundList.findIndex(
      (chipFund) => chipFund.id === id
    );

    investorFundList.splice(chipIndex, 1);
    setFundListUpdated(true);
  };

  return (
    <Container id="investor_funds_container" role="main">
      <Grid container>
        <Box sx={{ width: 300, maxHeight: 600, marginTop: "15px" }}>
          <Grid>Funds</Grid>
          <Typography variant="errorText" hidden={investorFundList.length > 0}>
            At least one fund is required
          </Typography>
          <StyledParentCard variant="outlined">
            <SearchBarInput
              id="inputSearch"
              size="small"
              placeholder="Search"
              onChange={handleOnChange}
              value={searchVal}
              InputProps={{
                startAdornment: (
                  <SearchInputAdornment position="start"></SearchInputAdornment>
                ),
                readOnly: false,
              }}
            />

            <StyledFormGroup>
              <FormControlLabel
                key="All"
                control={<Checkbox />}
                label="All"
                onChange={onCheckAll}
              />
            </StyledFormGroup>
            <StyledChecksCard>
              <FundContent id="fundContent">
                <FormGroup>
                  {checkFundList.map((fund) => (
                    <StyledFormControlLabel
                      key={fund.id}
                      control={
                        <Checkbox
                          id={fund.id}
                          name={fund.name}
                          checked={fund.isChecked}
                          onChange={onFundClick}
                        />
                      }
                      label={fund.name}
                    />
                  ))}
                </FormGroup>
              </FundContent>
            </StyledChecksCard>
          </StyledParentCard>
        </Box>
        <Grid item md={5} xs={8}>
          <Box
            sx={{
              marginTop: "35px",
              marginLeft: "10px",
            }}
          >
            <ChipsContent>
              <ChipGroup
                ChipTitleList={checkFundList.filter(
                  (fund) => fund.isChecked === true
                )}
                handleRemoveFilter={onRemoveChip}
                color={"green"}
              />
            </ChipsContent>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default InvestorFunds;
